import React, { useState, useEffect, useMemo } from 'react';
import { ESTATUS } from '../../config/config';
import { LogOut, Car, Search, Settings, X, FileDown, FileText } from 'lucide-react';
import { exportSalesDashboardExcel, exportSalesDashboardPDF } from '../../utils/exporters/salesDashboardExporter';
import VehiclesTable from '../layout/filters/VehiclesTable';
import { useTableColumns } from '../../hooks/useTableColumns';
import ColumnReorderPopup from '../popups/ColumnReorderPopup';
import axiosInstance, { filterVehiculosByPermissions } from '../../services/authService';

const ALL_COLUMNS = [
  { id: 'vin', label: 'VIN', sortable: true },
  { id: 'situacion', label: 'Situación', sortable: true },
  { id: 'marca', label: 'Marca', sortable: true },
  { id: 'familia', label: 'Familia', sortable: true },
  { id: 'catalogo', label: 'Catálogo', sortable: true },
  { id: 'anoModelo', label: 'Año', sortable: true },
  { id: 'equipamiento', label: 'Equipamiento', sortable: true },
  { id: 'descripcion', label: 'Descripción', sortable: true },
  { id: 'colorExt', label: 'Color Ext.', sortable: true },
  { id: 'colorInt', label: 'Color Int.', sortable: true },
  { id: 'sitio', label: 'Sitio', sortable: true },
  { id: 'subSitio', label: 'Subsitio', sortable: true },
  { id: 'diasAntiguedad', label: 'Días inv.', sortable: true },
  { id: 'fecha', label: 'Fecha Recibo', sortable: true },
  { id: 'observaciones', label: 'Observaciones', sortable: true },
];

const DEFAULT_SALES_COLUMN_IDS = ALL_COLUMNS.map(col => col.id);

const SalesDashboard = ({ userInfo, onLogout }) => {
  const [vehiculos, setVehiculos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState('Todos');

  const {
    visibleColumns: activeColumns,
    saveColumnOrder,
  } = useTableColumns('sales_dashboard_table', ALL_COLUMNS, DEFAULT_SALES_COLUMN_IDS);

  useEffect(() => {
    let mounted = true;

    const fetchVehiculos = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Obtenemos el inventario general móvil
        const responseMobile = await axiosInstance.get('/api/mobile/inventario-general/');

        // Obtenemos el comparativo completo para extraer las unidades "compradas por llegar" (estatus 3)
        const responseComp = await axiosInstance.get('/api/comparativo/').catch(() => ({ data: [] }));

        if (mounted) {
          const dataMobile = responseMobile.data?.vehiculos || responseMobile.data?.data || responseMobile.data;
          const listMobile = Array.isArray(dataMobile) ? dataMobile : [];

          const listComp = Array.isArray(responseComp.data) ? responseComp.data : [];

          // Filtramos las compradas por llegar que el usuario tiene permiso de ver
          const permitidosComp = filterVehiculosByPermissions(listComp);
          const porLlegar = permitidosComp.filter(v => v.estatus === ESTATUS.EN_TRANSITO);

          // Unimos evitando duplicados por VIN
          const combined = [...listMobile];
          const existingVins = new Set(combined.map(v => v.VIN));

          porLlegar.forEach(v => {
            if (!existingVins.has(v.VIN)) {
              combined.push(v);
            }
          });

          setVehiculos(combined);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          console.error('Error al cargar inventario móvil:', err);
          const errorMsg = err.response?.data
            ? (typeof err.response.data === 'object' ? JSON.stringify(err.response.data) : err.response.data)
            : err.message;
          setError(`Error ${err.response?.status || ''}: ${errorMsg}`);
          setIsLoading(false);
        }
      }
    };

    fetchVehiculos();
    return () => { mounted = false; };
  }, []);

  const familyStats = useMemo(() => {
    if (!vehiculos.length) return [];

    const stats = {};
    let total = 0;

    vehiculos.forEach(v => {
      total++;
      const fam = v.Familia || v.Modelo || 'OTROS';
      const year = v.Año || v.AnoModelo || 'N/A';

      if (!stats[fam]) stats[fam] = { total: 0, years: {} };
      stats[fam].total++;

      if (!stats[fam].years[year]) stats[fam].years[year] = 0;
      stats[fam].years[year]++;
    });

    const result = Object.entries(stats).map(([name, data]) => {
      const sortedYears = Object.entries(data.years)
        .sort(([yearA], [yearB]) => String(yearB).localeCompare(String(yearA)))
        .map(([year, count]) => `${year}(${count})`)
        .join(' ');

      return {
        name,
        total: data.total,
        subtitle: sortedYears
      };
    }).sort((a, b) => b.total - a.total);

    return [
      { name: 'Todos', total, subtitle: `${total} vehículos` },
      ...result
    ];
  }, [vehiculos]);

  const filteredVehiculos = useMemo(() => {
    let result = vehiculos;

    if (selectedFamily !== 'Todos') {
      result = result.filter(v => (v.Familia || v.Modelo || 'OTROS') === selectedFamily);
    }

    if (searchQuery.trim()) {
      const terms = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      result = result.filter(v => {
        const searchableText = Object.values(v)
          .map(val => (val !== null && val !== undefined ? String(val).toLowerCase() : ''))
          .join(' ');
        return terms.every(term => searchableText.includes(term));
      });
    }

    return result;
  }, [vehiculos, searchQuery, selectedFamily]);

  const handleExportExcel = () => exportSalesDashboardExcel(filteredVehiculos, activeColumns);
  const handleExportPDF = () => exportSalesDashboardPDF(filteredVehiculos, activeColumns);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <Car className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <span className="hidden sm:block text-xl font-bold text-gray-900 whitespace-nowrap">
                Dashboard de Inventario
              </span>
              <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 truncate max-w-[200px] sm:max-w-sm">
                {userInfo?.nombre_completo || userInfo?.username || 'Vendedor'}
              </span>
            </div>
            <button
              onClick={onLogout}
              className="flex-shrink-0 inline-flex items-center gap-2 px-2.5 py-2 sm:px-4 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Inventario Vehicular Disponible</h1>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200 flex-shrink-0"
              title="Exportar a Excel"
            >
              <FileDown className="w-4 h-4" />
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 flex-shrink-0"
              title="Exportar a PDF"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsColumnPopupOpen(true)}
              className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-300 flex-shrink-0"
              title="Configurar columnas"
            >
              <Settings className="w-4 h-4" />
            </button>
            <div className="w-full sm:w-72 lg:w-96 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar..."
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* --- FAMILY FILTER CHIPS --- */}
        <div className="mb-4 sm:mb-6 w-full overflow-x-auto pb-3
          [&::-webkit-scrollbar]:h-1.5 
          [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
          <div className="flex items-center gap-2 sm:gap-3">
            {familyStats.map(fam => (
              <button
                key={fam.name}
                onClick={() => setSelectedFamily(fam.name)}
                className={`flex-shrink-0 px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl text-center transition-all border ${selectedFamily === fam.name
                  ? 'bg-indigo-50 border-indigo-200 shadow-sm'
                  : 'bg-gray-100 border-transparent hover:bg-gray-200'
                  }`}
              >
                <div className={`text-sm sm:text-base font-bold tracking-tight ${selectedFamily === fam.name ? 'text-indigo-900' : 'text-gray-800'}`}>
                  {fam.name}
                </div>
                <div className={`text-[10px] sm:text-xs font-medium mt-0.5 ${selectedFamily === fam.name ? 'text-indigo-700' : 'text-gray-500'}`}>
                  {fam.subtitle}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Leyenda de colores */}
        <div className="flex justify-end mb-3 sm:mb-4 px-2">
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 sm:gap-x-6 sm:gap-y-1.5">
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-400 shrink-0" aria-hidden="true" />
              <span className="text-[9px] sm:text-xs font-medium text-gray-600">Demo</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-500">Cargando inventario...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <p>{error}</p>
            </div>
          ) : vehiculos.length > 0 ? (
            <VehiclesTable
              vehiculos={filteredVehiculos}
              visibleColumns={activeColumns}
              disableRowClick={true}
              itemsPerPage={50}
              isSalesDashboard={true}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Car className="h-12 w-12 text-gray-300 mb-4" />
              <p>No hay vehículos disponibles en inventario para este vendedor.</p>
            </div>
          )}
        </div>

        <ColumnReorderPopup
          isOpen={isColumnPopupOpen}
          onClose={() => setIsColumnPopupOpen(false)}
          allColumns={ALL_COLUMNS}
          currentColumns={activeColumns}
          onSave={saveColumnOrder}
          defaultColumnsIds={DEFAULT_SALES_COLUMN_IDS}
        />
      </main>
    </div>
  );
};

export default SalesDashboard;
